import { apiFetch } from '@nrdy-marketing-engine/api-fetch';
import { Address, PaymentMethodPayload } from 'braintree-web-drop-in';
import { CartState } from '../../../@types/cart-state';
import { CURRENCY_CODE_MAP } from '../../constants/currency-code-map';
import { jurisdictionToCountryCode } from '../../lib/country-code/jurisdiction-to-country-code';
import itemBuilder from './item-builder';
import paymentBuilder from './payment-builder';

type Payload = PaymentMethodPayload;
interface Student {
  email?: string;
  first_name?: string;
  uuid: string;
}

function isMobileApp() {
  return /TutoringTools/gi.test(window.navigator.userAgent);
}

function getPlatform() {
  return isMobileApp() ? 'mobile_app' : 'browser';
}

export function getSearchParam(param: string) {
  return new URLSearchParams(window.location.search).get(param);
}

function buildPayload(cartState: CartState, payload: Payload, student: Student) {
  const billingAddress =
    payload.type === 'PayPalAccount' ? payload.details.billingAddress : ({} as Address);
  const { client, cartJurisdictionId, lineItems } = cartState;
  const countryCode = jurisdictionToCountryCode(cartJurisdictionId);
  const quoteId = getSearchParam('q');

  const payment = paymentBuilder({
    billingAddress,
    countryCode,
    currencyCode: CURRENCY_CODE_MAP[countryCode],
    email: client.email,
    firstName: client.firstName,
    lastName: client.lastName,
    lineItems: lineItems || [],
    nonce: payload.nonce,
    numberOfPayments: 1,
    paymentType: payload.type,
    paymentAmount: cartState.cartPrice,
    phoneNumber: cartState.phoneNumber,
    zipCode: cartState.zipCode,
  });

  const promoCodePayload =
    cartState?.cart?.promo_codes !== null ? cartState?.cart?.promo_codes[0]?.id : null;

  const item = itemBuilder({
    catalogItem: cartState.catalogItem,
    name: student.first_name || client.firstName,
    email: student.email || client.email,
    studentId: student.uuid.toString(),
    currencyCode: CURRENCY_CODE_MAP[countryCode],
    price: cartState.cartPrice,
    promoId: promoCodePayload,
  });

  const quoteOrItem = quoteId ? { quote_id: quoteId } : { items: [item] };

  return {
    customer_type: 'client',
    customer_id: client.uuid.toString(),
    payment,
    phone: cartState.phoneNumber,
    ok_for_dialer_tou: cartState.agreeToTerms,
    platform: getPlatform(),
    student: {
      id: student.uuid.toString(),
      name: student.first_name || client.firstName,
      email: student.email || client.email,
    },
    ...quoteOrItem,
  };
}

interface MakePurchaseProps {
  cartState: CartState;
  payload: Payload;
  student: Student;
}

const makePurchase = async ({ cartState, payload, student }: MakePurchaseProps) => {
  const url = `/v1/clients/${cartState.client.uuid}/purchases?enroll_student=true`;
  return apiFetch(url, {
    method: 'POST',
    body: JSON.stringify(buildPayload(cartState, payload, student)),
  });
};

export default makePurchase;
